import React, { useRef, useMemo } from 'react';
import DropdownMenu from '../../common/Dropdown.jsx';
import { PortalTrigger } from '../../common/Portal';
import { Button } from '../../bootstrap';
import { Adalicon, FaIcon } from '../../common/Icons';
import LoadingIndicator from '../../common/loading_indicator';
import Tooltip from '../../common/Tooltip';
import useDiscussionDialog from '../../common/LiveDiscussion/useDiscussionDialog';
import useDiscussionMessage, {ADMIN_GROUPS, MESSAGE_STATUSES} from '../../common/LiveDiscussion/useDiscussionMessage';
import useDiscussionSettings from '../../common/LiveDiscussion/useDiscussionSettings';
import Scrollbars from "react-custom-scrollbars-2";
import LexicalDiscussionEditor, { BLANK_CONTENT } from '../../common/richTextPlugins/LexicalDiscussionEditor.lazy.jsx';
import { useQuery } from '@tanstack/react-query';
import { fetchJson } from '../../../helpers/fetch.js';
import ButtonSkeleton from '../../../skeleton_loaders/common/ButtonSkeleton.jsx';

import {
    live_discussion_project_report_total_message_path
} from '~/routes';

const COLOR_STYLE = {
    external: {
        header: '#006b99',
        body: '#eefaff',
        user: {
            self: '#006b99',
            project: '#4b84a5',
            fund: '#5e6163',
            other: '#d6f0fb'
        }
    },
    internal: {
        header: '#51586a',
        body: '#f6f6f6',
        user: {
            self: '#51586a',
            other: '#e5e6e8'
        }
    },
}

const USER_COLOR_STYLE = {
    external: {
        bubble: {
            self: '#006b99',
            other: '#d6f0fb'
        },
        name: {
            project: '#4b84a5',
            fund_staff: '#5e6163',
            admin: '#5e6163',
            fund_user: '#4b84a5',
            other: '#d6f0fb'
        }
    },
    internal: {
        bubble: {
            self: '#daefee',
            other: '#e5e6e8'
        },
        name: {
            other: 'black'
        }
    }
}

const DiscussionCount = ({ count }) => {
    return (count && count > 0) ? <span className="label label-warning">{count}</span> : null
}

const DiscussionButton = ({project_report_id, permissions, person_id, is_fund_staff, hide_staff_name, btnSize='md', project_id, dropup = false}) => {
    const {view_internal, view_external} = permissions
    const viewDropdown = view_external && view_internal

    const params = new URLSearchParams(window.location.search);
    const activeDiscussionStr = params.get("discussion_type");
    let defaultShow = null;
    if (['external', 'internal'].includes(activeDiscussionStr)){
        defaultShow = activeDiscussionStr
    }

    const {data: discussionMessages, isLoading} = useQuery({
        queryKey: ['project_report_total_messages', project_id, project_report_id, person_id],
        queryFn: async () => fetchJson(live_discussion_project_report_total_message_path({target_type: 'project_report', project_id: project_id, target_id: project_report_id})),
        enabled: !!project_report_id && !!person_id && !!project_id
    })
    const totalMessages = useMemo(() =>  Object.values(discussionMessages || {}).reduce((sum, val) => sum + val, 0),[discussionMessages])
    return(
        isLoading ? <ButtonSkeleton buttonContent={I18n.t('discussion')} /> :
        (viewDropdown ? 
            <DropdownMenu
            label={<><i className="fa-solid fa-comments" /> {I18n.t('discussion')} <DiscussionCount count={totalMessages} /><FaIcon type={dropup ? 'caret-up' : 'caret-down'} /></>}
            btnType="default"
            btnSize={btnSize}
            containerClass={`ml-1 d-inline-block project-report-actions ${dropup ? 'dropup' : ''}`}
            >
            {view_external &&
                <li key='external-discussion'>
                    <DiscussionThreadButton
                        label={<><i className='fa-solid fa-users' style={{color: '#006b99'}} /> {I18n.t('discuss_external')} <DiscussionCount count={discussionMessages['external']} /></>}
                        type='external'
                        btnType='link'
                        defaultShow={defaultShow === 'external'}
                        {...{project_report_id, permissions, person_id, is_fund_staff, hide_staff_name, project_id} }
                    />
                </li>
            }
            {view_internal &&
                <li key='internal-discussion'>
                    <DiscussionThreadButton
                        label={<><i className='fa-solid fa-shield-halved' style={{color: '#51586a'}} /> {I18n.t('discuss_internal')} <DiscussionCount count={discussionMessages['internal']} /></>}
                        type='internal'
                        btnType='link'
                        defaultShow={defaultShow === 'internal'}
                        {...{project_report_id, permissions, person_id, is_fund_staff, hide_staff_name, project_id} }
                    />
                </li>
            }
            </DropdownMenu> :
        <DiscussionThreadButton 
            label={<><i className="fa-solid fa-comments" /> {I18n.t('discussion')} <DiscussionCount count={totalMessages} /></>}
            btnType="default"
            btnSize={btnSize}
            btnClass="ml-1 d-inline-block project-report-actions"
            type={view_external ? 'external' : 'internal'}
            defaultShow={(defaultShow === 'external' && view_external) || (defaultShow === 'internal' && view_internal)}
            {...{project_report_id, permissions, person_id, is_fund_staff, hide_staff_name, project_id} }
        />)
    )
}

const DiscussionThreadButton = ({permissions, label, btnType, type, project_report_id, person_id, is_fund_staff, hide_staff_name, defaultShow, btnClass = '', btnSize, project_id}) => {
    return (
        <>
            <PortalTrigger
                label={label}
                portalProps={{
                    permissions,
                    type,
                    project_report_id,
                    project_id,
                    person_id,
                    is_fund_staff,
                    hide_staff_name,
                    component: DiscussionDialog
                }}
                btnProps={{
                    type: btnType,
                    className: btnClass,
                    size: btnSize
                }}
                defaultShow={defaultShow}
            />
        </>
    )
}

const DiscussionDialog = ({permissions, closeDialog, type, project_report_id, person_id, is_fund_staff, hide_staff_name, project_id}) => {
    const scrollRef = useRef(null);
    const {
        messages,
        handleScroll,
        cancelEditing,
        sendMessage,
        setContent,
        content,
        loading,
        setLoading,
        usersInfo,
        reloadMessages,
        editingId,
        setEditingId} = useDiscussionDialog({}, {target_type: 'project_report', target_id: project_report_id, discussion_type: type, scrollRef: scrollRef})

    const filePermissions = useMemo(() => ({
        [`view_${type}_discussion`]: {
            id: project_id,
            type: 'project'
        }
    }), [])

    return(
        <div className='discussion-dialog'>
            <div className='discussion-header' style={{backgroundColor: `${COLOR_STYLE[type].header}`}}>
                <div className='pull-left'>
                    <i className='fa-solid fa-comments' style={{marginRight: '0.5rem'}} />
                    {I18n.t(`discuss_${type}`)}
                    {type == 'internal' && <i className='fa-solid fa-shield-halved' style={{marginLeft: '0.5rem'}} />}
                </div>
                <div className='pull-right btn p-0' onClick={closeDialog}>
                    <i className='fa-solid fa-x' />
                </div>
            </div>
            <div className='discussion-body'>
                <Scrollbars onScroll={handleScroll} ref={scrollRef} style={{height: '100%', backgroundColor: `${COLOR_STYLE[type].body}`}}>
                        {
                            // Currently filter out deleted messages, can be shown if wanted, it's important because reloadMessages because on the oldestMessageId in the new messages from
                            // backend service. However, the deleted messages are gone so have mismatch -> Need to improve logic to remove deleted messages in backend query
                            loading && messages.length === 0 ? <LoadingIndicator size='sm' /> :
                            messages.filter((m) => m.status != 'deleted').map((m,i) => <DiscussionChatBubble
                                message={m} 
                                user={usersInfo[m.person_id]} 
                                owner={person_id === m.person_id}
                                key={`m-${m.id}`}
                                {...{permissions, type, hide_staff_name, loading, setLoading, setEditingId, setContent, reloadMessages, is_fund_staff}} />)
                        }
                </Scrollbars>
            </div>
            {
                permissions.reply_message &&
                <div className='discussion-chat-area'>
                    {editingId !== null && <div className='chat-editing-desc'><i className="fa fa-xs fa-pencil" style={{marginRight: '2px'}}></i>{I18n.t('editing_message')}</div>}
                    <LexicalDiscussionEditor
                        editorContainerStyle={{width: '100%', margin: 0}}
                        value={content} 
                        placeholder={I18n.t('type_your_message')} 
                        updateAnswer={(v) => setContent(v)}
                        onChange={(v) => setContent(v)} 
                        editorStyle={{backgroundColor: editingId !== null ? '#e5e6e8' : 'white', minHeight: '60px'}}
                        reset={content === ''}
                        editing={editingId !== null}
                        filePermissions={filePermissions}
                    />
                    <div className='chat-area-footer'>
                        {type === 'external' && <i className='pull-left'>{I18n.t('post_to_external')}</i>}
                        <Button type='primary' size='sm' onClick={sendMessage} disabled={loading || content.length === 0 || content === BLANK_CONTENT}>
                            {I18n.t('send')}
                        </Button>
                        {editingId !== null && <Button type='link' size='sm' className='' onClick={cancelEditing}>
                            {I18n.t('cancel')}   
                        </Button>}
                    </div>
                </div>
            }
        </div>
    )
}

const DiscussionChatBubble = ({owner, message, user = {}, ...rest}) => {
    const { setContent, setEditingId, hide_staff_name, is_fund_staff, type } = rest
    const {isHovered, setIsHovered, timeOut, user_logo_path} = useDiscussionMessage({user, owner, message, setContent, setEditingId,});
    const bubbleColor = owner ? USER_COLOR_STYLE[type].bubble.self : USER_COLOR_STYLE[type].bubble.other
    const bubbleName = Object.keys(USER_COLOR_STYLE[type]['name']).includes(user.user_group) ? USER_COLOR_STYLE[type]['name'][user.user_group] :  USER_COLOR_STYLE[type]['name']['other']
    const textColor = (owner && type == 'external') ? 'white' : 'black';
    const showName = is_fund_staff || type == 'internal' || (type == 'external' && !ADMIN_GROUPS.includes(user.user_group)) || (type == 'external' && ADMIN_GROUPS.includes(user.user_group) && !hide_staff_name);

    return (
        <div className='chat-bubble-container' onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <div className={`chat-bubble ${owner ? 'pull-right' : 'pull-left'}`}>
                <div className='bubble-header' style={{alignSelf: owner ? 'flex-end' : 'flex-start'}}>
                    <div>
                    { owner ? '' :  
                        showName ? 
                        <>
                            {user_logo_path && <img src={user_logo_path} alt=""/>}
                            <span style={{color: bubbleName}}>{`${user.full_name} - ${user.project_identifier}`}</span>
                        </> : 
                        I18n.t('tenant_team', {id: user.project_identifier})
                    }
                    </div>
                    <div>
                        {   message.status === MESSAGE_STATUSES['edited'] && message.updated_at !== null &&   
                            <Tooltip title={I18n.t('message_edited_at', {time: message.updated_at_label})} trigger='hover'>
                                <i className="fa fa-xs fa-pencil" style={{marginRight: '2px'}}></i>
                            </Tooltip>
                        }
                        <span className='color-grey'>{message.created_at_label}</span>
                    </div>
                </div>
                <LexicalDiscussionEditor
                    value={message.content}
                    editorContainerClassNames={`bubble-content message ${owner ? 'owner' : 'other'} `}
                    editorContainerStyle={{margin: 0, padding: 0}}
                    editorStyle={{backgroundColor: bubbleColor, color: textColor, alignSelf: owner ? 'end': 'start', minHeight: 0, padding: '0px 4px 0px 4px'}}
                    readOnly={true}
                    preview={true}
                    updateReadOnly={true}
                    transparentBackground={true}
                />
            </div>
            <div style={{width: '20%'}}>
                { isHovered && owner && !timeOut && <DiscussionSettings message={message} {...rest} />}
            </div>
        </div>
    )
}

const DiscussionSettings = ({message, owner, ...rest}) => {
    const {permissions, setLoading, loading, setEditingId, reloadMessages, setContent} = rest
    const {handleDeleteMessage, handleEditMessage} = useDiscussionSettings({message, reloadMessages, setLoading, setContent, setEditingId})
    const {edit_message, delete_message} = permissions

    return (
        <DropdownMenu
        label={<i className='fa fa-ellipsis-h'/>}
        btnType="link"
        btnSize="sm"
        containerClass='project-report-actions edit-bubble pull-right'
        classes='list-settings dropdown-chat-bubble-right'
        >
            <li key='edit-message'>
                <Button
                    disabled={!edit_message || loading}
                    type='link'
                    onClick={handleEditMessage}
                >
                    <Adalicon type="edit" /> {I18n.t("edit")}    
                </Button>
            </li>

            <li key='delete-message'>
                <Button
                    disabled={!delete_message || loading}
                    type="link"
                    onClick={handleDeleteMessage}
                >
                    <Adalicon type="trash" className="text-danger" /> {I18n.t("delete")}
                </Button>
            </li>
        </DropdownMenu>
    )
}

export default DiscussionButton;